"use client";
import React, { useState } from "react";
import { Save, Trash2, Eye, CheckCircle2, AlertCircle, LayoutTemplate, List, Search, Box, X } from "lucide-react";

type ActionType = "save" | "delete" | "view" | "list" | null;

const ACTIONS = {
    save: {
        title: "Save Project",
        description: "Persist your current workspace state to the cloud. This ensures all your schema changes, node positions, and settings are safely stored.",
        feedback: "Project saved successfully!",
        color: "text-green-500",
        bg: "bg-green-500/10",
        border: "border-green-500/20"
    },
    delete: {
        title: "Clear Workspace",
        description: "Remove all nodes and reset the workspace to a blank state. This action cannot be undone, so use it with caution when you want to start fresh.",
        feedback: "Workspace cleared",
        color: "text-red-500",
        bg: "bg-red-500/10",
        border: "border-red-500/20"
    },
    view: {
        title: "View Mode",
        description: "Toggle between Edit and View modes. View mode locks the canvas to prevent accidental changes while you review your schema architecture.",
        feedback: "Switched to View Mode",
        color: "text-blue-500",
        bg: "bg-blue-500/10",
        border: "border-blue-500/20"
    },
    list: {
        title: "All Nodes",
        description: "Quickly navigate your schema with the Nodes Drawer. Search for specific tables and instantly zoom to their location on the canvas.",
        feedback: "Opened Nodes List",
        color: "text-purple-500",
        bg: "bg-purple-500/10",
        border: "border-purple-500/20"
    }
};

const MOCK_NODES = [
    { id: "1", name: "Users", type: "Auth" },
    { id: "2", name: "Orders", type: "Table" },
    { id: "3", name: "Products", type: "Table" },
    { id: "4", name: "Categories", type: "Table" },
    { id: "5", name: "Profiles", type: "One-to-One" },
    { id: "6", name: "Logs", type: "System" },
];

export const InteractiveToolbar = () => {
    const [activeAction, setActiveAction] = useState<ActionType>("save");
    const [showFeedback, setShowFeedback] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [zoomedNode, setZoomedNode] = useState<string | null>(null);

    const handleAction = (action: ActionType) => {
        setActiveAction(action);
        if (action === 'list') {
            setIsDrawerOpen(true);
        } else {
            setIsDrawerOpen(false);
            setShowFeedback(true);
            setTimeout(() => setShowFeedback(false), 2000);
        }
    };

    const handleNodeClick = (nodeName: string) => {
        setZoomedNode(nodeName);
        setTimeout(() => setZoomedNode(null), 1500); // Reset zoom effect
    };

    const filteredNodes = MOCK_NODES.filter(n => n.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const currentAction = activeAction ? ACTIONS[activeAction] : ACTIONS.save;

    return (
        <div className="w-full bg-surface/40 backdrop-blur-md border border-border/50 rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row h-[500px]">
            {/* Left Side: Dynamic Text */}
            <div className="flex-1 p-8 lg:p-12 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-border/50 relative overflow-hidden">
                {/* Background decoration */}
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent transition-opacity duration-500 ${activeAction ? 'opacity-100' : 'opacity-0'}`} />

                <div className="relative z-10">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-6 transition-colors duration-300 ${currentAction.bg} ${currentAction.color} border ${currentAction.border}`}>
                        {activeAction === 'save' && <Save className="w-3 h-3" />}
                        {activeAction === 'delete' && <Trash2 className="w-3 h-3" />}
                        {activeAction === 'view' && <Eye className="w-3 h-3" />}
                        {activeAction === 'list' && <List className="w-3 h-3" />}
                        {currentAction.title}
                    </div>

                    <h3 className="text-3xl font-bold text-primary-text mb-4 transition-all duration-300">
                        {currentAction.title}
                    </h3>
                    <p className="text-secondary-text text-lg leading-relaxed transition-all duration-300">
                        {currentAction.description}
                    </p>
                </div>
            </div>

            {/* Right Side: Toolbar Mockup */}
            <div className="flex-1 bg-background/50 relative overflow-hidden flex flex-col items-center justify-center">
                {/* Grid Pattern */}
                <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] transition-transform duration-700 ${zoomedNode ? "scale-150" : "scale-100"}`} />

                {/* Feedback Toast */}
                <div className={`absolute top-8 left-1/2 -translate-x-1/2 bg-surface border border-border shadow-lg rounded-full px-4 py-2 flex items-center gap-2 transition-all duration-300 z-20 ${showFeedback ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                    {activeAction === 'save' && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                    {activeAction === 'delete' && <AlertCircle className="w-4 h-4 text-red-500" />}
                    {activeAction === 'view' && <LayoutTemplate className="w-4 h-4 text-blue-500" />}
                    <span className="text-sm font-medium text-primary-text">{currentAction.feedback}</span>
                </div>

                {/* Zoom Indicator */}
                <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 ${zoomedNode ? "opacity-100" : "opacity-0"}`}>
                    <div className="bg-primary/10 backdrop-blur-sm border border-primary/20 rounded-xl p-4 flex flex-col items-center animate-in zoom-in-50 duration-300">
                        <Search className="w-8 h-8 text-primary mb-2" />
                        <span className="text-lg font-bold text-primary">Zooming to {zoomedNode}...</span>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="bg-surface border border-border rounded-xl shadow-xl p-2 flex items-center gap-2 relative z-10 scale-110">
                    <button
                        onClick={() => handleAction('save')}
                        className={`p-3 rounded-lg transition-all duration-200 group relative ${activeAction === 'save' ? 'bg-green-500/10 text-green-500' : 'hover:bg-primary/5 text-secondary-text hover:text-primary-text'}`}
                    >
                        <Save className="w-6 h-6" />
                    </button>

                    <div className="w-px h-8 bg-border mx-1" />

                    <button
                        onClick={() => handleAction('delete')}
                        className={`p-3 rounded-lg transition-all duration-200 group relative ${activeAction === 'delete' ? 'bg-red-500/10 text-red-500' : 'hover:bg-primary/5 text-secondary-text hover:text-primary-text'}`}
                    >
                        <Trash2 className="w-6 h-6" />
                    </button>

                    <div className="w-px h-8 bg-border mx-1" />

                    <button
                        onClick={() => handleAction('view')}
                        className={`p-3 rounded-lg transition-all duration-200 group relative ${activeAction === 'view' ? 'bg-blue-500/10 text-blue-500' : 'hover:bg-primary/5 text-secondary-text hover:text-primary-text'}`}
                    >
                        <Eye className="w-6 h-6" />
                    </button>

                    <div className="w-px h-8 bg-border mx-1" />

                    <button
                        onClick={() => handleAction('list')}
                        className={`p-3 rounded-lg transition-all duration-200 group relative ${activeAction === 'list' ? 'bg-purple-500/10 text-purple-500' : 'hover:bg-primary/5 text-secondary-text hover:text-primary-text'}`}
                    >
                        <List className="w-6 h-6" />
                    </button>
                </div>

                {/* Side Drawer */}
                <div className={`absolute top-0 right-0 h-full w-64 bg-surface/95 backdrop-blur-xl border-l border-border shadow-2xl transition-transform duration-300 z-30 flex flex-col ${isDrawerOpen ? "translate-x-0" : "translate-x-full"}`}>
                    <div className="p-4 border-b border-border flex items-center justify-between">
                        <h4 className="font-bold text-primary-text">All Nodes</h4>
                        <button onClick={() => setIsDrawerOpen(false)} className="p-1 hover:bg-primary/10 rounded text-secondary-text hover:text-primary-text transition-colors">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="p-4 border-b border-border">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-text" />
                            <input
                                type="text"
                                placeholder="Search nodes..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-background border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-primary-text focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {filteredNodes.map(node => (
                            <button
                                key={node.id}
                                onClick={() => handleNodeClick(node.name)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors text-left group"
                            >
                                <div className="w-8 h-8 rounded-lg bg-background border border-border flex items-center justify-center group-hover:border-primary/30 transition-colors">
                                    <Box className="w-4 h-4 text-secondary-text group-hover:text-primary transition-colors" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-primary-text group-hover:text-primary transition-colors">{node.name}</div>
                                    <div className="text-xs text-secondary-text">{node.type}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
